import { CloudOff as CloudOffIcon, WifiOff as WifiOffIcon } from 'components/icons';
import { buttonStyle } from 'components/buttons/buttonStyle';
import LogoMain from 'components/logo/LogoMain';
import { Button } from 'components/ui/button';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { isSorainenEnv } from 'utils/env';

export type ApiDowntimeType = 'api-down' | 'offline';

interface ApiDowntimePageProps {
    type: ApiDowntimeType;
    onRetry: () => void;
}

const ApiDowntimePage = ({ type, onRetry }: ApiDowntimePageProps) => {
    const isOffline = type === 'offline';
    const Icon = isOffline ? WifiOffIcon : CloudOffIcon;
    const titleId = useId();
    const descriptionId = useId();
    const overlayRef = useRef<HTMLDivElement>(null);
    const retryButtonRef = useRef<HTMLButtonElement>(null);

    // Dialogs, drawers and popovers portal into document.body, outside the app root, so every
    // other body child is made inert — otherwise an open modal form keeps its focus trap and
    // stays editable under the overlay. That includes portals a late-settling request opens
    // while the overlay is up. Children that are already inert belong to someone else, such as
    // an open modal, and are left alone — unless the owner releases them before service returns.
    useEffect(() => {
        const previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        const blocked = new Set<Element>();
        const block = (element: Element) => {
            if (element !== overlayRef.current && !element.hasAttribute('inert')) {
                element.setAttribute('inert', '');
                blocked.add(element);
            }
        };

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes') {
                    block(mutation.target as Element);
                }
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element) {
                        watch(node);
                    }
                });
            });
            retryButtonRef.current?.focus();
        });
        const watch = (element: Element) => {
            block(element);
            observer.observe(element, { attributes: true, attributeFilter: ['inert'] });
        };

        Array.from(document.body.children).forEach(watch);
        observer.observe(document.body, { childList: true });
        retryButtonRef.current?.focus();

        return () => {
            observer.disconnect();
            blocked.forEach((element) => element.removeAttribute('inert'));
            previouslyFocused?.focus();
        };
    }, []);

    return createPortal(
        <div
            ref={overlayRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            className="bg-bg/95 fixed inset-0 z-(--z-blocking-loader) flex flex-col items-center justify-center overflow-y-auto px-6 text-center"
        >
            <div className="mb-8">
                <LogoMain />
            </div>

            <Icon className="text-fg-secondary mb-6 text-[64px]" />

            <h4 id={titleId} className="text-h4 mb-3 text-balance">
                <FormattedMessage id={isOffline ? 'api-downtime.offline-title' : 'api-downtime.api-down-title'} />
            </h4>

            <p id={descriptionId} className="text-body1 text-fg-secondary mb-8 max-w-[480px]">
                <FormattedMessage
                    id={
                        isOffline
                            ? isSorainenEnv
                                ? 'api-downtime.offline-description-sorainen'
                                : 'api-downtime.offline-description'
                            : 'api-downtime.api-down-description'
                    }
                />
            </p>

            <Button ref={retryButtonRef} {...buttonStyle({ variant: 'contained' })} onClick={onRetry}>
                <FormattedMessage id="api-downtime.retry" />
            </Button>

            <span className="text-caption text-fg-disabled mt-6">
                <FormattedMessage id="api-downtime.auto-retry" />
            </span>
        </div>,
        document.body
    );
};

export default ApiDowntimePage;
